import { useCallback, useState } from "react";

import {
  FormulaTraitConfig,
  TraitType,
} from "@hightouch/lib/query/visual/types/trait-definitions";
import {
  RelatedColumn,
  TransformedColumn,
} from "@hightouch/lib/query/visual/types";
import { TRAIT_PREVIEW_ALIAS } from "@hightouch/lib/query/visual/util";
import { useToast } from "@hightouch/htui";

import { useVisualQuerySqlQuery } from "src/graphql";

import { PreviewableTrait } from "src/components/traits/use-preview-trait";

export const useTraitSqlPreview = ({
  parentModel,
  trait,
  useSampledModels,
}: {
  parentModel: {
    id: string;
    connection?: { id: string } | null;
  };
  trait: PreviewableTrait;
  useSampledModels?: boolean;
}) => {
  const { toast } = useToast();

  const traitColumn: RelatedColumn | TransformedColumn =
    // Tracked-audience-membership traits have no relationship; reference the
    // saved trait definition directly with an empty join path so the backend
    // reads the audience_membership changelog.
    trait.type === TraitType.TrackedAudienceMembership
      ? {
          type: "related",
          column: {
            type: "trait",
            traitDefinitionId: trait.traitDefinitionId,
            conditions: [],
          },
          path: [],
        }
      : trait.type !== TraitType.Formula && trait.relationshipId
        ? {
            type: "related",
            column: {
              type: "inline_trait",
              traitType: trait.type,
              traitConfig: trait.config,
              conditions: trait.conditions ?? [],
              relationshipId: trait.relationshipId,
            },
            path: [trait.relationshipId],
          }
        : {
            type: "transformed",
            column: {
              type: "inline_trait",
              traitType: trait.type as TraitType.Formula,
              traitConfig: trait.config as FormulaTraitConfig,
            },
          };

  const visualQueryFilter = {
    conditions: [],
    additionalColumns: [
      {
        alias: TRAIT_PREVIEW_ALIAS,
        column: traitColumn,
      },
    ],
  };

  const { refetch: refetchVisualQuerySql } = useVisualQuerySqlQuery(
    {
      connection_id: parentModel.connection?.id?.toString() ?? "",
      parent_model_id: parentModel.id?.toString() ?? "",
      filter: visualQueryFilter,
      useSampledModels,
    },
    {
      enabled: false,
      meta: { isFastEndpoint: true },
    },
  );

  const queryKey = JSON.stringify({
    filter: visualQueryFilter,
    useSampledModels,
  });

  const [transformedSql, setTransformedSql] = useState<{
    sql: string | undefined | null;
    queryKey: string | null;
    loading: boolean;
    visible: boolean;
  }>({
    sql: undefined,
    queryKey: null,
    loading: false,
    visible: false,
  });

  const showSql = useCallback(async () => {
    if (transformedSql.sql && transformedSql.queryKey === queryKey) {
      setTransformedSql((state) => ({ ...state, visible: true }));
      return;
    }

    setTransformedSql((state) => ({
      ...state,
      loading: true,
      visible: true,
    }));
    try {
      const result = await refetchVisualQuerySql();
      setTransformedSql((state) => ({
        ...state,
        sql: result?.data?.visualQuerySQL?.sql,
        queryKey,
        loading: false,
      }));
    } catch (error) {
      setTransformedSql((state) => ({
        ...state,
        loading: false,
        visible: false,
      }));
      toast({
        id: "sql-preview",
        title: "SQL preview failed",
        message: error instanceof Error ? error.message : "Unknown error",
        variant: "error",
      });
    }
  }, [
    refetchVisualQuerySql,
    toast,
    queryKey,
    transformedSql.sql,
    transformedSql.queryKey,
  ]);

  const hideSql = useCallback(() => {
    setTransformedSql((state) => ({ ...state, visible: false }));
  }, []);

  const copySql = useCallback(() => {
    if (transformedSql.sql) {
      navigator.clipboard.writeText(transformedSql.sql);
      toast({
        id: "copy-sql",
        title: "SQL copied to clipboard",
        variant: "success",
      });
    }
  }, [transformedSql.sql, toast]);

  return {
    sql: transformedSql.sql,
    loading: transformedSql.loading,
    visible: transformedSql.visible,
    showSql,
    hideSql,
    copySql,
  };
};
